import React, { useEffect, useMemo, useRef, useState } from "react";
import TaskCard from "@components/TaskCard";
import CreateTaskModal from "@components/modals/CreateTaskModal";
import TaskLogsModal from "@components/modals/TaskLogsModal";
import TaskSettingsModal from "@components/modals/TaskSettingsModal";
import TaskStatsModal from "@components/modals/TaskStatsModal";
import { useTasks } from "@hooks/useTasks";
import { tasksApi } from "@api/tasks";
import { statsApi } from "@api/stats";

function Pagination() {
  return (
    <div className="mt-10 flex items-center justify-center gap-3">
      <button className="w-10 h-10 rounded-full bg-white shadow-[0_10px_25px_rgba(0,0,0,0.08)] grid place-items-center text-[#6B7280] hover:text-[#111827] transition">
        ‹
      </button>
      <div className="flex items-center gap-3 text-sm">
        <button className="w-8 h-8 rounded-full grid place-items-center text-[#2B7CFF] font-semibold">1</button>
        <button className="w-8 h-8 rounded-full grid place-items-center text-[#6B7280] hover:text-[#111827] transition">2</button>
        <button className="w-8 h-8 rounded-full grid place-items-center text-[#6B7280] hover:text-[#111827] transition">3</button>
        <span className="text-[#9CA3AF] px-2">…</span>
        <button className="w-9 h-9 rounded-full grid place-items-center text-[#6B7280] hover:text-[#111827] transition">32</button>
      </div>
      <button className="w-10 h-10 rounded-full bg-white shadow-[0_10px_25px_rgba(0,0,0,0.08)] grid place-items-center text-[#6B7280] hover:text-[#111827] transition">
        ›
      </button>
    </div>
  );
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}
function digitsOnly(s) {
  return String(s ?? "").replace(/[^\d]/g, "");
}
function getAccountsCount(t) {
  return (t?.taskAccountUids?.length ?? 0) || (t?.runtime?.assignedAccountUids?.length ?? 0) || 0;
}
function getDoneCount(t) {
  const m = t?.runtime?.doneByAccount;
  let sum = 0;
  if (m && typeof m === "object") {
    for (const v of Object.values(m)) sum += Number(v) || 0;
  }
  if (!sum) sum = Number(t?.accountsAdded) || 0;
  return Math.max(0, Math.floor(sum));
}
function isWithinNumberFilter(value, mode, threshold) {
  if (!threshold && threshold !== 0) return true;
  const v = Number(value) || 0;
  const th = Number(threshold);
  if (!Number.isFinite(th)) return true;
  if (mode === "gt") return v > th;
  if (mode === "lt") return v < th;
  if (mode === "gte") return v >= th;
  if (mode === "lte") return v <= th;
  return true;
}

/** ---------- FilterDropdown ---------- */
function FilterDropdown({ open, anchorRef, value, onChange, onClose }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const panel = panelRef.current;
      const anchor = anchorRef?.current;
      if (!panel) return;
      if (panel.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const statuses = [
    { key: "waiting", label: "waiting" },
    { key: "running", label: "running" },
    { key: "pause", label: "pause" },
    { key: "stopped", label: "stopped" },
    { key: "done", label: "done" },
    { key: "error", label: "error" },
  ];

  const setStatusChecked = (k, checked) => {
    const next = new Set(value.statuses || []);
    if (checked) next.add(k); else next.delete(k);
    onChange({ ...value, statuses: Array.from(next) });
  };

  const reset = () => {
    onChange({ progressMode: "gte", progressValue: "", statuses: [], accountsMode: "gte", accountsValue: "" });
  };

  const hasAny =
    (value.progressValue !== "" && value.progressValue != null) ||
    (value.accountsValue !== "" && value.accountsValue != null) ||
    (value.statuses?.length || 0) > 0;

  const box = "rounded-[16px] border border-[#E5E7EB] bg-white shadow-[0_18px_40px_rgba(0,0,0,0.12)]";
  const label = "text-[12px] font-semibold text-[#374151]";
  const hint = "text-[11px] text-[#6B7280]";
  const select = "h-10 rounded-[14px] border border-[#D6D9E0] bg-white px-3 outline-none focus:border-[#2B7CFF] text-[13px]";
  const input = "h-10 w-full rounded-[14px] border border-[#D6D9E0] bg-white px-3 outline-none focus:border-[#2B7CFF] text-[13px]";

  return (
    <div ref={panelRef} className={`absolute right-0 top-[48px] w-[360px] ${box} p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[14px] font-semibold text-[#111827]">Фильтры</div>
          <div className={`mt-0.5 ${hint}`}>Можно комбинировать условия.</div>
        </div>
        <button type="button" onClick={onClose}
          className="w-9 h-9 rounded-full bg-[#F3F4F6] grid place-items-center hover:bg-[#EDEFF3] transition">
          <i className="fa-solid fa-xmark text-[#111827]" />
        </button>
      </div>

      <div className="mt-4 grid gap-4">
        <div className="grid gap-2">
          <div className="flex items-center justify-between">
            <div className={label}>Прогресс</div>
            <div className={hint}>0–100%</div>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <select className={select} value={value.progressMode}
              onChange={(e) => onChange({ ...value, progressMode: e.target.value })}>
              <option value="gte">больше/равно</option>
              <option value="lte">меньше/равно</option>
              <option value="gt">больше</option>
              <option value="lt">меньше</option>
            </select>
            <div className="relative">
              <input className={`${input} pr-8`} inputMode="numeric"
                value={value.progressValue}
                onChange={(e) => onChange({ ...value, progressValue: digitsOnly(e.target.value).slice(0, 3) })}
                onBlur={() => {
                  if (value.progressValue === "") return;
                  const v = clamp(Number(value.progressValue || 0), 0, 100);
                  onChange({ ...value, progressValue: String(v) });
                }}
                placeholder="например 30" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#6B7280]">%</span>
            </div>
          </div>
        </div>

        <div className="grid gap-2">
          <div className={label}>Статус</div>
          <div className="grid grid-cols-2 gap-2">
            {statuses.map((s) => {
              const checked = (value.statuses || []).includes(s.key);
              return (
                <label key={s.key}
                  className="flex items-center gap-2 rounded-[14px] border border-[#E5E7EB] px-3 py-2 hover:bg-[#F9FAFB] transition cursor-pointer">
                  <input type="checkbox" checked={checked}
                    onChange={(e) => setStatusChecked(s.key, e.target.checked)}
                    className="accent-[#2B7CFF]" />
                  <span className="text-[13px] text-[#111827]">{s.label}</span>
                </label>
              );
            })}
          </div>
          <div className={hint}>Если ничего не выбрано — подходят все.</div>
        </div>

        <div className="grid gap-2">
          <div className={label}>Аккаунтов в задаче</div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <select className={select} value={value.accountsMode}
              onChange={(e) => onChange({ ...value, accountsMode: e.target.value })}>
              <option value="gte">больше/равно</option>
              <option value="lte">меньше/равно</option>
              <option value="gt">больше</option>
              <option value="lt">меньше</option>
            </select>
            <input className={input} inputMode="numeric"
              value={value.accountsValue}
              onChange={(e) => onChange({ ...value, accountsValue: digitsOnly(e.target.value).slice(0, 7) })}
              placeholder="например 50" />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <button type="button" onClick={reset}
            className={`h-10 px-4 rounded-full border border-[#D6D9E0] bg-white hover:bg-[#F7F8FA] transition text-sm font-medium ${!hasAny ? "opacity-50 pointer-events-none" : ""}`}>
            Сбросить
          </button>
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-full bg-[#2B7CFF] text-white shadow-[0_12px_28px_rgba(43,124,255,0.35)] hover:brightness-95 transition text-sm font-semibold">
            Применить
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { tasks, loading, refetch } = useTasks({ pollMs: 2500 });

  const [statsByTask, setStatsByTask] = useState({});
  const [dailyStatsByTask, setDailyStatsByTask] = useState({});
  // ✅ Реальный прогресс из account_groups — обновляется каждые 5 секунд
  const [totalProgressByTask, setTotalProgressByTask] = useState({});

  // Загрузка статистики за сегодня (из логов)
  useEffect(() => {
    let alive = true;
    const uids = (tasks || []).map((t) => Number(t.uid)).filter((x) => Number.isFinite(x));
    if (!uids.length) { setStatsByTask({}); return () => {}; }

    const load = async () => {
      try {
        const data = await statsApi.todayByTask(uids);
        if (alive) setStatsByTask(data?.tasks || {});
      } catch { /* ignore */ }
    };

    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [tasks]);

  // Ежедневная статистика из задачи
  // ✅ Зависим от строки uids — не перезапускается при каждом поллинге tasks (каждые 2.5с)
  const taskUidsKey = (tasks || []).map((t) => t.uid).join(",");
  useEffect(() => {
    let alive = true;
    const uidsArr = taskUidsKey ? taskUidsKey.split(",").map(Number).filter(Boolean) : [];
    if (!uidsArr.length) return;

    const loadDailyStats = async () => {
      for (const uid of uidsArr) {
        try {
          const dailyStats = await tasksApi.dailyStats(uid);
          // ✅ Обновляем по одному — не затираем всё если один запрос упал
          if (alive) setDailyStatsByTask((prev) => ({ ...prev, [String(uid)]: dailyStats }));
        } catch { /* ignore */ }
      }
    };

    loadDailyStats();
    const t = setInterval(loadDailyStats, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [taskUidsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ Реальный прогресс из account_groups — каждые 5 секунд
  useEffect(() => {
    let alive = true;
    const uids = (tasks || []).map((t) => Number(t.uid)).filter((x) => Number.isFinite(x));
    if (!uids.length) { setTotalProgressByTask({}); return () => {}; }

    const load = async () => {
      try {
        const data = await statsApi.totalProgressBatch(uids);
        if (alive) setTotalProgressByTask(data?.tasks || {});
      } catch { /* ignore */ }
    };

    load();
    const t = setInterval(load, 5000); // ✅ каждые 5 секунд
    return () => { alive = false; clearInterval(t); };
  }, [tasks]);

  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [logsUid, setLogsUid] = useState(null);
  const [settingsUid, setSettingsUid] = useState(null);
  const [statsUid, setStatsUid] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterBtnRef = useRef(null);
  const [filters, setFilters] = useState({
    progressMode: "gte", progressValue: "", statuses: [], accountsMode: "gte", accountsValue: "",
  });

  const activeTask = useMemo(
    () => tasks.find((t) => t.uid === settingsUid) || null,
    [tasks, settingsUid],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return tasks.filter((t) => {
      if (s) {
        const name = String(t.taskName || "").toLowerCase();
        if (!name.includes(s)) return false;
      }
      if (filters.statuses?.length) {
        const st = String(t.status || "");
        if (!filters.statuses.includes(st)) return false;
      }
      if (filters.progressValue !== "" && filters.progressValue != null) {
        // ✅ Используем реальный прогресс для фильтра если есть
        const realProgress = totalProgressByTask?.[String(t.uid)];
        const p = realProgress ? realProgress.progressPct : (t.progressValue || 0);
        if (!isWithinNumberFilter(p, filters.progressMode, filters.progressValue)) return false;
      }
      if (filters.accountsValue !== "" && filters.accountsValue != null) {
        const a = Number((t.taskAccountUids?.length ?? 0) || (t.runtime?.assignedAccountUids?.length ?? 0) || 0);
        if (!isWithinNumberFilter(a, filters.accountsMode, filters.accountsValue)) return false;
      }
      return true;
    });
  }, [tasks, q, filters, totalProgressByTask]);

  const filterBadgeCount = useMemo(() => {
    let c = 0;
    if (filters.progressValue !== "" && filters.progressValue != null) c += 1;
    if (filters.accountsValue !== "" && filters.accountsValue != null) c += 1;
    if ((filters.statuses?.length || 0) > 0) c += 1;
    return c;
  }, [filters]);

  const handleCreate = async ({ taskName, startTime }) => {
    if (!taskName) return;
    await tasksApi.create({ taskName, startTime });
    setCreateOpen(false);
    refetch();
  };

  const handleStart = async (uid) => { await tasksApi.start(uid); refetch(); };
  const handlePause = async (uid) => { await tasksApi.pause(uid); refetch(); };
  const handleStop = async (uid) => { await tasksApi.stop(uid); refetch(); };
  const handleResume = async (uid) => { await tasksApi.resume(uid); refetch(); };

  return (
    <div className="min-h-screen">
      <main className="px-6 pb-14">
        <div className="mx-auto max-w-[1220px]">
          <div className="mt-10 flex items-center justify-between gap-6">
            <h1 className="text-[28px] font-semibold text-[#111827]">Список задач</h1>

            <div className="flex items-center gap-3 relative">
              <div className="h-10 w-[360px] border border-[#C8C8C8] bg-white rounded-full shadow-[0_10px_25px_rgba(0,0,0,0.06)] px-4 flex items-center gap-2">
                <i className="fa-solid fa-magnifying-glass text-[#9CA3AF] w-6" />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                  className="w-full outline-none text-sm placeholder:text-[#9CA3AF]"
                  placeholder="Поиск по названию задачи" />
              </div>

              <button ref={filterBtnRef} onClick={() => setFilterOpen((v) => !v)}
                className="relative h-10 px-4 border border-[#C8C8C8] bg-white rounded-full shadow-[0_10px_25px_rgba(0,0,0,0.06)] flex items-center gap-2 text-sm font-medium text-[#111827] hover:opacity-90 transition"
                type="button">
                <i className="fa-solid fa-arrow-up-short-wide h-3" />
                Фильтр
                {filterBadgeCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE]">
                    {filterBadgeCount}
                  </span>
                )}
              </button>

              <FilterDropdown
                open={filterOpen} anchorRef={filterBtnRef}
                value={filters} onChange={setFilters} onClose={() => setFilterOpen(false)} />

              <button
                className="relative h-10 pl-12 pr-5 rounded-full bg-[#2B7CFF] text-white shadow-[0_12px_28px_rgba(43,124,255,0.35)] text-sm font-semibold hover:brightness-95 transition flex items-center"
                onClick={() => setCreateOpen(true)} type="button">
                <span className="absolute left-4 flex items-center">
                  <span className="w-6 h-6 rounded-full bg-white/20 grid place-items-center">
                    <i className="fa-solid fa-list-check w-4 h-3 text-[12px]" />
                  </span>
                </span>
                <span className="mx-auto leading-none">Создать задачу</span>
              </button>
            </div>
          </div>

          <div className="mt-3 text-[12px] text-[#6B7280]">
            Найдено:{" "}
            <span className="font-semibold text-[#111827]">{filtered.length}</span>
            {q.trim() && (
              <> • запрос:{" "}
                <span className="font-semibold text-[#111827]">"{q.trim()}"</span>
              </>
            )}
          </div>

          <div className="mt-7 grid grid-cols-2 gap-6">
            {loading && <div className="text-sm text-[#6B7280]">Загрузка...</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-sm text-[#6B7280]">Ничего не найдено - попробуй изменить поиск/фильтры.</div>
            )}

            {filtered.map((t) => {
              // ✅ Используем реальный прогресс из account_groups если доступен
              const realProgress = totalProgressByTask?.[String(t.uid)];
              const pct = realProgress ? realProgress.progressPct : (t.progressValue || 0);
              const done = realProgress ? realProgress.totalSubscriptions : getDoneCount(t);
              const total = realProgress ? realProgress.totalTarget : (getAccountsCount(t) * (t.groupsValue || 0));

              // ✅ "сегодня" и "среднее": берём из doneByAccount напрямую (всегда актуально,
              // не зависит от временного диапазона логов и не обнуляется при status=done)
              const doneByAccount = t?.runtime?.doneByAccount || {};
              const doneValues = Object.values(doneByAccount).map((v) => Number(v) || 0);
              const sessionTotal = doneValues.reduce((sum, v) => sum + v, 0);
              const sessionUniqueAccounts = doneValues.filter((v) => v > 0).length;
              const sessionAvg = sessionUniqueAccounts > 0
                ? Number((sessionTotal / sessionUniqueAccounts).toFixed(2))
                : 0;

              // Если doneByAccount пустой (задача ещё не запускалась сегодня) — используем данные из лог-запроса
              const logStats = statsByTask?.[String(t.uid)];
              const statsToday = sessionTotal > 0
                ? {
                    totalSubscriptionsToday: sessionTotal,
                    uniqueAccountsToday: sessionUniqueAccounts,
                    avgSubscriptionsPerAccountToday: sessionAvg,
                  }
                : logStats ?? {
                    totalSubscriptionsToday: 0,
                    uniqueAccountsToday: 0,
                    avgSubscriptionsPerAccountToday: 0,
                  };

              return (
                <TaskCard
                  key={t.uid}
                  uid={t.uid}
                  title={t.taskName}
                  dStatsSub={t.accountsAdded}
                  status={t.status}
                  accounts={getAccountsCount(t)}
                  groups={t.groupsValue || 0}
                  progressValue={pct}
                  accountsAdded={done}
                  progressTotal={total}
                  statsToday={statsToday}
                  dailyStats={dailyStatsByTask?.[String(t.uid)] ?? null}
                  onLogs={(uid) => setLogsUid(uid)}
                  onSettings={(uid) => setSettingsUid(uid)}
                  onStats={(uid) => setStatsUid(uid)}
                  onStart={handleStart}
                  onPause={handlePause}
                  onStop={handleStop}
                  onResume={handleResume}
                  onEdit={(uid) => setSettingsUid(uid)}
                />
              );
            })}
          </div>
        </div>
      </main>

      <CreateTaskModal open={createOpen} onClose={() => setCreateOpen(false)} onSubmit={handleCreate} />
      <TaskLogsModal open={logsUid != null} onClose={() => setLogsUid(null)} taskUid={logsUid} />
      <TaskSettingsModal
        open={settingsUid != null} onClose={() => setSettingsUid(null)}
        task={activeTask} onSaved={refetch} onDeleted={refetch} onPaused={refetch} onResumed={refetch} />
      <TaskStatsModal
        open={statsUid != null} onClose={() => setStatsUid(null)}
        taskUid={statsUid} taskName={tasks.find((t) => t.uid === statsUid)?.taskName} />
    </div>
  );
}
